/**
 * SOUND-643 Phase 3 — related-record rollups for the company record home.
 *
 * Overview was four cards and seven fields: name, type, tier, slug, id, created,
 * updated. It said nothing about what the company actually HAS. A Salesforce-style
 * record home leads with the related records, so this defines what to count,
 * where to count it, and where clicking lands.
 *
 * NOT an N+1: four fixed requests on a detail page, one per entity type,
 * regardless of how many records exist. SOUND-295 is about list pages fetching
 * per visible ROW, which is a different problem.
 */

/**
 * Each rollup names its endpoint, the query that scopes it to one company, and
 * the Company Hub tab it links to.
 *
 * `?companyId=` is honoured for INTERNAL callers on all four endpoints; for a
 * client or partner it is ignored and getScopeFilter pins the request to their
 * own company anyway, so the same call is correct for every audience.
 */
export const COMPANY_ROLLUPS = [
  { key: "mids", label: "MIDs", path: "mids", tab: "mids" },
  {
    key: "midApplications",
    label: "MID Applications",
    path: "mid-applications",
    tab: "mid-applications",
  },
  { key: "tickets", label: "Tickets", path: "tickets", tab: "tickets" },
  { key: "files", label: "Files", path: "files", tab: "files" },
];

/**
 * Read a count out of a list response.
 *
 * All four endpoints now return `{ data, pagination: { total } }` — `/files`
 * joined them in SOUND-661, which is why this reads `pagination.total` first.
 * The array fallback stays because it is the correct answer for an unpaginated
 * response and costs nothing: `/files` pagination is OPT-IN, so a caller that
 * omits page/limit still gets a bare full list alongside a real total.
 *
 * The order matters. Counting `data.length` first would report the PAGE size as
 * the total the moment any caller paginates — the kind of wrong number nobody
 * questions because it looks like an answer.
 */
export function readCount(payload) {
  if (!payload) return 0;
  const total = payload?.pagination?.total;
  if (typeof total === "number") return total;
  if (Array.isArray(payload?.data)) return payload.data.length;
  if (Array.isArray(payload)) return payload.length;
  return 0;
}

/**
 * Where clicking a rollup lands. Tab keys are the ones CompanyDetails registers
 * (`mids`, `mid-applications`, `tickets`, `files`).
 */
export function rollupHref(companyId, tab) {
  return `/companies/${companyId}?tab=${tab}`;
}

/**
 * Build display descriptors from a counts map. Missing entries render as a dash
 * rather than 0 — "no data yet" and "zero records" are different claims, and on
 * a record home the difference decides whether someone goes looking.
 */
export function buildRollups(companyId, counts = {}, { loading = false } = {}) {
  return COMPANY_ROLLUPS.map((r) => {
    const count = counts[r.key];
    const known = typeof count === "number";
    return {
      ...r,
      count: known ? count : null,
      display: loading || !known ? "—" : String(count),
      href: rollupHref(companyId, r.tab),
      empty: known && count === 0,
    };
  });
}
